// src/components/partials/ZelloSetupWizard/ZelloSetupWizardModal.tsx
/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import styled from '@emotion/styled'
import { Button, CircularProgress, Dialog, IconButton, MenuItem, Select } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { ConfirmationDialog } from 'components/partials'
import { StyledFormLabel, StyledInput } from 'components/widgets'
import { getAppColors, GRANTS, PRIMARY_COLOR } from 'lib/constants'
import { getNameFromUser } from 'lib/utils'
import type { Organization, User } from 'lib/types'
import { Grants, Users } from 'models'
import { enableVoiceForOrg, type EnableVoiceParams } from 'models/api/voice'
import { rowContainerStyles, titleTextStyles } from 'styles'

const StyledButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '8px 20px',
  border: `2px solid ${PRIMARY_COLOR}`,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat',
  boxShadow: '0px 4px 8px rgba(20, 48, 72, 0.1)',
  fontSize: '1.0rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginRight: '0px',
  cursor: 'pointer',
  borderRadius: '2px',
  '&.Mui-disabled': {
    opacity: 0.5,
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
})

const StatusMessage = styled.span<{ success: boolean }>`
  margin-left: 1rem;
  margin-top: 0px;
  color: ${props => (props.success ? PRIMARY_COLOR : '#d32f2f')};
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  align-self: center;
  font-size: 1.2rem;
  flex: 1 1 0;
  min-width: 0;
`

const WarningBox = styled.div({
  border: '1px solid rgba(48, 188, 176, 0.35)',
  background: 'rgba(48, 188, 176, 0.08)',
  borderRadius: 8,
  padding: '10px 12px',
  fontFamily: 'Montserrat',
  fontSize: '0.95rem',
  lineHeight: '1.3rem',
  color: getAppColors('--color-text'),
})

const CompactInput = styled(StyledInput)({
  borderRadius: '10px',
  minHeight: 44,
  paddingTop: 0,
  paddingBottom: 0,

  '&.MuiInputBase-root': {
    padding: '6px 12px',
  },

  '& input': {
    padding: 0,
    height: 32,
    fontSize: '1.05rem',
    lineHeight: '1.2rem',
    fontFamily: 'Montserrat',
  },
})

const CompactSelect = styled(Select)({
  borderRadius: '10px',
  minHeight: 44,
  fontFamily: 'Montserrat',
  fontSize: '1.05rem',
  background: getAppColors('--color-surface'),
  border: `1px solid ${getAppColors('--color-border')}`,
  padding: '0 12px',
  textTransform: 'none',

  '& .MuiSelect-select': {
    padding: '10px 0',
    minHeight: 'unset',
    textTransform: 'none',
  },
  '& .MuiSelect-select:focus': {
    background: 'transparent',
  },
})

const CompactRow = styled.div({
  marginTop: 10,
  marginBottom: 10,
})

const normalizeSubdomain = (name: string) => name.toLowerCase().replace(/\s+/g, '')

const buildInitialForm = (org: Organization | null): EnableVoiceParams => ({
  admin_email: '',
  admin_name: 'admin',
  company: org?.name || '',
  subdomain: normalizeSubdomain(org?.name || ''),
})

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
}

type Props = {
  open: boolean
  onClose: () => void
  org: Organization | null
  onOrgUpdated?: (org: Organization) => void
  disabledBecauseConfigured?: boolean
}

export default function ZelloSetupWizardModal({
  open,
  onClose,
  org,
  onOrgUpdated,
  disabledBecauseConfigured = false,
}: Props) {
  const [form, setForm] = useState<EnableVoiceParams>(() => buildInitialForm(org))
  const [loading, setLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusSuccess, setStatusSuccess] = useState<boolean | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const allGrants = Grants.use(({ grants }) => grants)
  const usersById = Users.use(({ usersById }) => usersById)

  const adminUserIds = useMemo<string[]>(() => {
    if (!org?.id) return []
    const ids = new Set<string>()
    for (const g of allGrants) {
      if (g.attributes.orgId === org.id && g.attributes.name === GRANTS.ADMIN) {
        ids.add(g.attributes.userId)
      }
    }
    return Array.from(ids)
  }, [allGrants, org?.id])

  useEffect(() => {
    if (!open) return
    adminUserIds.forEach(uid => {
      if (!usersById[uid]) Users.reloadUser(uid)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adminUserIds])

  const adminUsers = useMemo<User[]>(() => {
    return adminUserIds
      .map(id => usersById[id])
      .filter((u): u is User => Boolean(u && u.attributes?.email))
      .sort((a, b) => {
        const an = getNameFromUser(a) || a.attributes.email
        const bn = getNameFromUser(b) || b.attributes.email
        return an.localeCompare(bn)
      })
  }, [adminUserIds, usersById])

  useEffect(() => {
    if (!open) return
    setForm(buildInitialForm(org))
    setLoading(false)
    setStatusMessage(null)
    setStatusSuccess(null)
    setConfirmOpen(false)
  }, [open, org])

  useEffect(() => {
    if (!open) return
    if (form.admin_email) return
    const first = adminUsers[0]
    if (first?.attributes?.email) {
      setForm(prev => (prev.admin_email ? prev : { ...prev, admin_email: first.attributes.email }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adminUsers])

  const canSubmit = useMemo(() => {
    if (!org?.id) return false
    if (disabledBecauseConfigured) return false

    const requiredFilled =
      form.admin_email.trim() && form.admin_name.trim() && form.company.trim() && form.subdomain.trim()

    if (!requiredFilled) return false
    if (!isValidEmail(form.admin_email)) return false
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(form.subdomain.trim())) return false

    return true
  }, [org?.id, form, disabledBecauseConfigured])

  const handleChange = (key: keyof EnableVoiceParams) => (e: ChangeEvent<HTMLInputElement>) => {
    setStatusMessage(null)
    setStatusSuccess(null)
    setForm(prev => ({ ...prev, [key]: e.target.value }))
  }

  const handleAdminEmailChange = (e: ChangeEvent<{ value: unknown }>) => {
    setStatusMessage(null)
    setStatusSuccess(null)
    setForm(prev => ({ ...prev, admin_email: String(e.target.value || '') }))
  }

  const startSubmit = () => {
    if (!canSubmit || loading) return
    setConfirmOpen(true)
  }

  const doSubmit = async () => {
    if (!org?.id) return

    setConfirmOpen(false)
    setLoading(true)
    setStatusMessage(null)
    setStatusSuccess(null)

    try {
      const payload = await enableVoiceForOrg(org.id, {
        admin_email: form.admin_email.trim(),
        admin_name: form.admin_name.trim(),
        company: form.company.trim(),
        subdomain: form.subdomain.trim(),
      })

      if (payload && typeof payload === 'object' && onOrgUpdated) {
        onOrgUpdated(payload as Organization)
      }

      setStatusMessage('Voice enabled successfully.')
      setStatusSuccess(true)
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status
      const errors = err?.response?.data?.errors ?? err?.response?.data
      const errorTitle = Array.isArray(errors) ? errors[0]?.title : errors?.[0]?.title
      const message =
        status === 422
          ? `Server responded with 422. ${errorTitle || ''}`.trim()
          : errorTitle || err?.message || 'Failed to enable voice.'
      setStatusMessage(message)
      setStatusSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  const subdomainError = form.subdomain.trim().length > 0 && !/^[a-z0-9][a-z0-9-]*$/i.test(form.subdomain.trim())

  return (
    <>
      <ConfirmationDialog
        title={'Enable Voice (Zello)'}
        description={
          'These credentials and settings cannot be changed after they are set. Are you sure you want to enable voice for this org?'
        }
        open={confirmOpen}
        onConfirm={doSubmit}
        handleClose={() => setConfirmOpen(false)}
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            maxWidth: 560,
            borderRadius: 10,
          },
        }}
      >
        <div style={{ padding: '1.25rem', position: 'relative' }}>
          <IconButton onClick={onClose} style={{ position: 'absolute', top: 6, right: 6 }} aria-label="close">
            <CloseIcon />
          </IconButton>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.0rem' }}>
            <h3 css={titleTextStyles} style={{ margin: 0 }}>
              {'Zello Setup Wizard'}
            </h3>
            {disabledBecauseConfigured ? (
              <StatusMessage success={false}>{'Already configured (immutable)'}</StatusMessage>
            ) : null}
          </div>

          <div css={rowContainerStyles} style={{ marginBottom: '0.9rem' }}>
            <WarningBox>
              <strong>Important:</strong> Once enabled, these Zello settings <strong>cannot be changed</strong>. Double
              check everything before submitting.
            </WarningBox>
          </div>

          <div css={rowContainerStyles} style={{ marginBottom: 0 }}>
            <CompactRow>
              <StyledFormLabel>
                {'Admin Email'}
                <CompactSelect
                  id="zello-admin-email"
                  fullWidth
                  disableUnderline
                  displayEmpty
                  value={form.admin_email}
                  onChange={handleAdminEmailChange as any}
                >
                  <MenuItem value="" disabled style={{ textTransform: 'none' }}>
                    {adminUsers.length === 0 ? 'No admin users found for this org' : 'Select an admin user'}
                  </MenuItem>
                  {adminUsers.map(u => {
                    const name = getNameFromUser(u)
                    const label = name ? `${name} (${u.attributes.email})` : u.attributes.email
                    return (
                      <MenuItem key={u.id} value={u.attributes.email} style={{ textTransform: 'none' }}>
                        {label}
                      </MenuItem>
                    )
                  })}
                </CompactSelect>
              </StyledFormLabel>
            </CompactRow>

            <CompactRow>
              <StyledFormLabel>
                {'Admin Name'}
                <CompactInput
                  id="zello-admin-name"
                  required
                  disableUnderline
                  fullWidth
                  value={form.admin_name}
                  onChange={handleChange('admin_name')}
                  inputProps={{ maxLength: 255 }}
                />
              </StyledFormLabel>
            </CompactRow>

            <CompactRow>
              <StyledFormLabel>
                {'Company'}
                <CompactInput
                  id="zello-company"
                  required
                  disableUnderline
                  fullWidth
                  readOnly
                  value={form.company}
                  inputProps={{ maxLength: 255, readOnly: true, 'aria-readonly': true }}
                />
              </StyledFormLabel>
            </CompactRow>

            <CompactRow>
              <StyledFormLabel>
                {'Subdomain'}
                <CompactInput
                  id="zello-subdomain"
                  required
                  disableUnderline
                  fullWidth
                  value={form.subdomain}
                  onChange={handleChange('subdomain')}
                  inputProps={{ maxLength: 63 }}
                />
                {subdomainError ? (
                  <div style={{ color: 'red', marginTop: 6 }}>
                    {'Use only letters/numbers/dashes. Must start with a letter/number.'}
                  </div>
                ) : null}
              </StyledFormLabel>
            </CompactRow>
          </div>

          <div
            css={rowContainerStyles}
            style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.9rem' }}
          >
            <StyledButton onClick={onClose} disabled={loading}>
              {'Cancel'}
            </StyledButton>

            <StyledButton onClick={startSubmit} disabled={loading || !canSubmit}>
              {loading ? <CircularProgress size={18} style={{ marginRight: 8 }} /> : null}
              {'Enable Voice'}
            </StyledButton>

            {statusMessage ? <StatusMessage success={statusSuccess ?? false}>{statusMessage}</StatusMessage> : null}
          </div>
        </div>
      </Dialog>
    </>
  )
}
